import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Container, Spinner, EmptyState } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { formatTime, formatPrice, formatDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fileUrl } from "@/components/FileUpload";
import { MapPin, Clock, Car, Accessibility, Youtube, FileText, ListMusic, Archive, ChevronDown, Users } from "lucide-react";

function DanceRow({ d }) {
  return (
    <Link to={`/dances/${d.slug}`} data-testid={`history-dance-${d.slug}`} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-secondary">
      <span className="font-semibold text-[#2D1E18]">{d.name}</span>
      <LevelBadge level={d.level} />
    </Link>
  );
}

function TermHistory({ slug, termId }) {
  const { data, isLoading } = useQuery({ queryKey: ["term", slug, termId], queryFn: async () => (await api.get(`/classes/${slug}/terms/${termId}`)).data });
  if (isLoading) return <div className="px-4 pb-4"><Spinner /></div>;
  const h = data?.history || [];
  if (!h.length) return <p className="px-4 pb-4 text-sm text-[#6E5B51]">No dances were recorded for this course.</p>;
  return (
    <div className="border-t border-border px-4 py-3">
      {h.map((g) => (
        <div key={g.date} className="mb-3 last:mb-0">
          <p className="mb-1 text-sm font-bold text-[#B04B26]">{formatDate(g.date, { year: true })}</p>
          {g.dances.map((x) => <DanceRow key={x.dance.id} d={x.dance} />)}
        </div>
      ))}
    </div>
  );
}

function PastCourses({ slug, terms, embedded }) {
  const [openId, setOpenId] = useState(null);
  return (
    <div className={embedded ? "" : "mt-10"} data-testid="past-courses">
      {!embedded && <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-[#2D1E18]"><Archive size={20} className="text-[#6E5B51]" /> Past courses</h2>}
      <p className="text-sm text-[#6E5B51]">Previous runs of this course — every dance taught is still here to browse.</p>
      <div className="mt-4 space-y-2">
        {terms.map((t) => (
          <div key={t.id} className="overflow-hidden rounded-2xl border border-border bg-card">
            <button onClick={() => setOpenId(openId === t.id ? null : t.id)} data-testid={`past-term-${t.id}`}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-secondary">
              <span className="font-semibold text-[#2D1E18]">{t.name}</span>
              <span className="flex items-center gap-2 text-sm text-[#6E5B51]">{formatDate(t.start_date)}{t.end_date ? ` – ${formatDate(t.end_date)}` : ""}<ChevronDown size={16} className={`transition-transform ${openId === t.id ? "rotate-180" : ""}`} /></span>
            </button>
            {openId === t.id && <TermHistory slug={slug} termId={t.id} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaylistBlock({ pl, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const n = pl.items.length;
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`played-${pl.id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`played-toggle-${pl.id}`}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-secondary">
        <span className="flex items-center gap-2 font-semibold text-[#2D1E18]"><ListMusic size={18} className="text-[#B04B26]" /> {formatDate(pl.session?.date, { year: true })}</span>
        <span className="flex items-center gap-2 text-sm text-[#6E5B51]">{n} dance{n !== 1 ? "s" : ""}<ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && (
        <div className="border-t border-border p-4">
          {pl.notes && <p className="mb-3 text-sm text-[#5C4A42]">{pl.notes}</p>}
          <ol className="space-y-2">
            {pl.items.map((it, i) => (
              <li key={i} className="flex items-center gap-3 rounded-xl border border-border bg-secondary/40 p-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#C85A32] text-sm font-bold text-white">{i + 1}</span>
                <Link to={`/dances/${it.dance.slug}`} className="flex-1 font-semibold text-[#2D1E18] hover:text-[#B04B26]">{it.dance.name}</Link>
                {it.tag && <span className="rounded-full bg-[#2D1E18] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{it.tag}</span>}
                {it.plays > 1 && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-[#5C4A42]">×{it.plays}</span>}
                <LevelBadge level={it.dance.level} />
                {it.dance.tutorial_url && <a href={it.dance.tutorial_url} target="_blank" rel="noopener noreferrer" aria-label="Tutorial" className="text-[#B04B26]"><Youtube size={18} /></a>}
                {(it.dance.step_sheet_url || it.dance.copperknob_url) && <a href={it.dance.step_sheet_url || it.dance.copperknob_url} target="_blank" rel="noopener noreferrer" aria-label="Step sheet" className="text-[#6E5B51]"><FileText size={18} /></a>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

const ACT = {
  taught: { label: "Taught", cls: "bg-[#C85A32] text-white" },
  reviewed: { label: "Reviewed", cls: "bg-[#FEF3C7] text-[#92600A]" },
  danced: { label: "Danced", cls: "bg-secondary text-[#5C4A42]" },
};

function HistRow({ x }) {
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2" data-testid={`history-dance-${x.dance.slug}`}>
      <Link to={`/dances/${x.dance.slug}`} className="flex-1 font-semibold text-[#2D1E18] hover:text-[#B04B26]">{x.dance.name}</Link>
      {(x.activities || []).map((a) => ACT[a] && <span key={a} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${ACT[a].cls}`}>{ACT[a].label}</span>)}
      {x.tag && <span className="rounded-full bg-[#2D1E18] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{x.tag}</span>}
      {x.plays > 1 && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-[#5C4A42]">×{x.plays}</span>}
      <LevelBadge level={x.dance.level} />
    </li>
  );
}

function ClassHistory({ c, slug }) {
  const [view, setView] = useState("term");
  const [taughtOnly, setTaughtOnly] = useState(false);
  const hasPrev = (c.past_terms?.length || 0) > 0;
  const { data: allData, isLoading: allLoading } = useQuery({
    queryKey: ["allhist", slug], enabled: view === "all",
    queryFn: async () => (await api.get(`/classes/${slug}/history`)).data,
  });
  const tl = view === "all" ? (allData || []) : (c.timeline || []);
  const views = [
    { k: "term", label: "This Term" },
    ...(hasPrev ? [{ k: "previous", label: "Previous Terms" }] : []),
    { k: "all", label: "All History" },
  ];
  return (
    <div data-testid="class-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-[#2D1E18]"><ListMusic size={20} className="text-[#B04B26]" /> Class history</h2>
          <p className="text-sm text-[#6E5B51]">{c.current_term && view === "term" ? <>Current course: <span className="font-semibold text-[#5C4A42]">{c.current_term.name}</span> · </> : null}Everything taught, reviewed, danced and played — newest first.</p>
        </div>
        {view !== "previous" && (
          <button onClick={() => setTaughtOnly((v) => !v)} data-testid="history-taught-only"
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold ${taughtOnly ? "bg-[#C85A32] text-white" : "border border-border bg-white text-[#5C4A42]"}`}>
            {taughtOnly ? "Showing taught only" : "Show taught only"}
          </button>
        )}
      </div>
      {views.length > 1 && (
        <div className="mt-3 inline-flex rounded-full border border-border bg-secondary/40 p-1" role="tablist" aria-label="Class history range" data-testid="history-view-switch">
          {views.map((v) => (
            <button key={v.k} role="tab" aria-selected={view === v.k} data-testid={`history-view-${v.k}`}
              onClick={() => setView(v.k)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${view === v.k ? "bg-[#C85A32] text-white" : "text-[#5C4A42] hover:bg-secondary"}`}>
              {v.label}
            </button>
          ))}
        </div>
      )}
      {view === "previous" ? (
        <div className="mt-4"><PastCourses slug={slug} terms={c.past_terms} embedded /></div>
      ) : allLoading ? <div className="mt-4"><Spinner /></div> : !tl.length ? (
        <div className="mt-4"><EmptyState title="No class history yet" subtitle="Dances will appear here after each class." /></div>
      ) : (
        <div className="mt-4 space-y-3">
          {tl.map((g) => {
            const dances = taughtOnly ? g.dances.filter((x) => (x.activities || []).includes("taught")) : g.dances;
            if (!dances.length) return null;
            return (
              <div key={g.date} className="rounded-2xl border border-border bg-card p-4" data-testid={`history-date-${g.date}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-[#B04B26]">{formatDate(g.date, { weekday: "short", year: true })}</p>
                  {g.has_playlist && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#6E5B51]"><ListMusic size={13} /> Playlist</span>}
                </div>
                {g.notes && <p className="mb-2 text-sm text-[#5C4A42]">{g.notes}</p>}
                <ul className="space-y-1.5">{dances.map((x) => <HistRow key={x.dance.id} x={x} />)}</ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ClassDetail() {
  const { slug } = useParams();
  const { data: c, isLoading, isError } = useQuery({ queryKey: ["class", slug], queryFn: async () => (await api.get(`/classes/${slug}`)).data });

  if (isLoading) return <Spinner />;
  if (isError || !c) return <Container className="py-20"><EmptyState title="Class not found" subtitle="This class may have moved or been archived." /></Container>;

  return (
    <div>
      <div className="border-b border-border bg-gradient-to-b from-[#F4EFE6] to-[#FAF7F2]">
        <Container className="py-10">
          <Link to="/classes" className="text-sm font-semibold text-[#B04B26] hover:underline">← All classes</Link>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-extrabold text-[#2D1E18] sm:text-4xl">{c.name}</h1>
            <LevelBadge level={c.level} />
          </div>
          {c.temporary_notice && <p className="mt-3 inline-block rounded-lg bg-[#FEF3C7] px-3 py-1.5 text-sm font-semibold text-[#92600A]">{c.temporary_notice}</p>}
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[#5C4A42]">
            <span className="inline-flex items-center gap-2"><Clock size={16} className="text-[#B04B26]" /> {c.day}, {formatTime(c.start_time)} – {formatTime(c.finish_time)}</span>
            <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-[#B04B26]" /> {c.venue?.name}</span>
            <span className="font-bold text-[#B04B26]">{formatPrice(c.price)} per class</span>
          </div>
        </Container>
      </div>

      <Container className="grid grid-cols-1 gap-8 py-10 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          {c.photo_path && <img src={fileUrl(c.photo_path)} alt={c.name} className="w-full rounded-2xl border border-border object-cover" style={{ maxHeight: 360 }} data-testid="class-photo" />}
          {c.description && <p className="text-lg leading-relaxed text-[#5C4A42]">{c.description}</p>}

          <ClassHistory c={c} slug={slug} />
        </div>

        <aside className="space-y-6">
          {c.instructors?.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="class-instructors">
              <h3 className="font-display text-lg font-bold text-[#2D1E18]">{c.instructors.length > 1 ? "Your instructors" : "Your instructor"}</h3>
              <div className="mt-4 space-y-4">
                {c.instructors.map((it) => (
                  <div key={it.id} className="flex items-center gap-3" data-testid={`class-instructor-${it.slug || it.id}`}>
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-[#EBE1D8]">
                      {it.photo_path ? <img src={fileUrl(it.photo_path)} alt={it.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-[#C9B8AC]"><Users size={22} /></div>}
                    </div>
                    <div>
                      <Link to="/instructors" className="font-semibold text-[#2D1E18] hover:text-[#B04B26]">{it.name}</Link>
                      {it.title && <p className="text-sm text-[#6E5B51]">{it.title}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {c.venue && (
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold text-[#2D1E18]">{c.venue.name}</h3>
              <p className="mt-1 text-sm text-[#5C4A42]">{c.venue.address}{c.venue.town ? `, ${c.venue.town}` : ""} {c.venue.postcode}</p>
              {c.venue.parking && <p className="mt-3 flex items-start gap-2 text-sm text-[#5C4A42]"><Car size={16} className="mt-0.5 shrink-0 text-[#6E5B51]" /> {c.venue.parking}</p>}
              {c.venue.accessibility && <p className="mt-2 flex items-start gap-2 text-sm text-[#5C4A42]"><Accessibility size={16} className="mt-0.5 shrink-0 text-[#6E5B51]" /> {c.venue.accessibility}</p>}
              {c.venue.postcode && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.venue.name + " " + c.venue.postcode)}`} target="_blank" rel="noopener noreferrer" data-testid="venue-directions" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C85A32] px-4 py-2.5 font-semibold text-white hover:bg-[#B04B26]"><MapPin size={16} /> Get directions</a>}
            </div>
          )}
        </aside>
      </Container>
    </div>
  );
}
